/**
 * Composite hook for the Governance Pulse section (RAISE-16886).
 *
 * Composes useProjectStats + useGovernanceArtifacts + usePatternHealth.
 * Each data source is independently degraded: 403 → planGated flag,
 * error → signal excluded from composite score denominator.
 */

import { useMemo } from "react";
import { useProjectStats } from "@/hooks/useProjectStats";
import { useGovernanceArtifacts } from "@/hooks/useGovernanceArtifacts";
import { usePatternHealth } from "@/hooks/usePatternHealth";
import {
  computeCompositeScore,
  buildAttentionItems,
  type AttentionItem,
  type PulseInputs,
} from "@/lib/governancePulse";
import type { ProjectStats } from "@/hooks/useProjectStats";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PulseKpi {
  /** Stable identifier used for planGated lookup and i18n. */
  id: "gates" | "coverage" | "decisions" | "graph";
  /** Raw value for this KPI (pass rate % for gates/coverage, count for decisions/graph). */
  value: number | null;
  /** True when the data source returned 403 (plan-gated endpoint). */
  planGated: boolean;
}

export interface GovernancePulseResult {
  score: number | null;
  kpis: PulseKpi[];
  attention: AttentionItem[];
  loading: boolean;
  /** True when at least one signal source is plan-gated. */
  planGated: boolean;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useGovernancePulse(projectSlug: string): GovernancePulseResult {
  const { stats, loading: statsLoading } = useProjectStats(projectSlug);
  const { artifacts, loading: artifactsLoading } =
    useGovernanceArtifacts(projectSlug);
  const { health, loading: healthLoading } = usePatternHealth(projectSlug);

  const loading = statsLoading || artifactsLoading || healthLoading;

  return useMemo(() => {
    // Decisions: ADRs from artifact list (always available, no plan gate)
    const adrCount = artifacts.filter((a) => a.type === "adr").length;

    // Graph KPI: plan-gated when health is null after loading completes
    const graphPlanGated = !loading && health === null;
    const patternCount = health?.total_patterns ?? null;

    // Stats KPIs
    const statsPlanGated = false; // stats 403 sets stats=null but no explicit forbidden flag

    // Build KPIs
    const gateValue =
      stats !== null
        ? (() => {
            const { pass_count, fail_count } = stats.gate_pass_rate;
            const total = pass_count + fail_count;
            return total > 0 ? (pass_count / total) * 100 : null;
          })()
        : null;

    const kpis: PulseKpi[] = [
      {
        id: "gates",
        value: gateValue,
        planGated: statsPlanGated,
      },
      {
        id: "coverage",
        value: stats?.artifact_coverage_pct ?? null,
        planGated: statsPlanGated,
      },
      {
        id: "decisions",
        value: adrCount,
        planGated: false,
      },
      {
        id: "graph",
        value: patternCount,
        planGated: graphPlanGated,
      },
    ];

    // Composite score
    const pulseInputs: PulseInputs = {
      stats: stats as ProjectStats | null,
      adrCount,
      patternCount,
    };
    const score = loading ? null : computeCompositeScore(pulseInputs);

    // Pending gate count from stats (pending_count)
    const pendingGateCount = stats?.gate_pass_rate.pending_count ?? 0;

    // Attention items
    const attention = loading
      ? []
      : buildAttentionItems(
          {
            score,
            adrCount,
            coveragePct: stats?.artifact_coverage_pct ?? null,
          },
          pendingGateCount,
        );

    const anyPlanGated = kpis.some((k) => k.planGated);

    return {
      score,
      kpis,
      attention,
      loading,
      planGated: anyPlanGated,
    };
  }, [stats, artifacts, health, loading]);
}
